import React, { useState, useEffect, useRef } from 'react';
import {
  X, Mail, Loader2, CheckCircle, Rocket, ArrowRight,
  Sparkles, Target, Users, Brain, Flame, Trophy, Zap, Star,
  Download, ChevronDown, Heart, TrendingUp, BookOpen
} from 'lucide-react';
import { saveEmail } from './firebase';

const APP_STORE_URL = 'https://apps.apple.com/app/id6746683535';

// Phone mockup component
const PhoneMockup = ({ src, className = '', style = {} }) => (
  <div className={`relative ${className}`} style={{ width: 280, height: 606, ...style }}>
    <div className="absolute inset-0 rounded-[2rem] bg-[#1a1a1a] shadow-2xl shadow-black/40 p-[4px]">
      <div className="relative w-full h-full rounded-[1.8rem] overflow-hidden bg-black">
        <img
          src={src}
          alt="GoalHive app"
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      </div>
    </div>
  </div>
);

// App Store button
const AppStoreButton = ({ className = '', dark = true }) => (
  <a
    href={APP_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center h-16 px-7 gap-3 rounded-2xl font-semibold hover:scale-105 active:scale-95 transition-all duration-200 shadow-xl ${
      dark
        ? 'bg-[#0a0a0a] text-white shadow-black/20'
        : 'bg-white text-[#0a0a0a] shadow-white/20'
    } ${className}`}
  >
    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
    <div className="text-left">
      <div className="text-[10px] font-normal opacity-70 leading-none">Download on the</div>
      <div className="text-xl leading-tight">App Store</div>
    </div>
  </a>
);

const App7 = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeScreen, setActiveScreen] = useState(0);
  const featurePauseRef = useRef(null);
  const screenPauseRef = useRef(null);
  const [featurePaused, setFeaturePaused] = useState(false);
  const [screenPaused, setScreenPaused] = useState(false);
  const heroRef = useRef(null);

  // Load + scroll observer
  useEffect(() => {
    setIsLoaded(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.dataset.section) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.12 }
    );

    // Small delay to ensure all elements are rendered
    requestAnimationFrame(() => {
      document.querySelectorAll('[data-section]').forEach(el => observer.observe(el));
    });

    return () => observer.disconnect();
  }, []);

  // Mouse parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Pause and resume helpers
  const pauseFeatures = () => {
    setFeaturePaused(true);
    clearTimeout(featurePauseRef.current);
    featurePauseRef.current = setTimeout(() => setFeaturePaused(false), 8000);
  };

  const pauseScreens = () => {
    setScreenPaused(true);
    clearTimeout(screenPauseRef.current);
    screenPauseRef.current = setTimeout(() => setScreenPaused(false), 8000);
  };

  // Auto-cycle features
  useEffect(() => {
    if (featurePaused) return;
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(interval);
  }, [featurePaused]);

  // Auto-cycle screenshots
  const screens = [
    { src: '/goal_based_feed.webp', title: 'Goal-Based Feed', description: 'A feed built around goals, not noise' },
    { src: '/public_profile_with_goals.webp', title: 'Your Profile', description: 'Show off your goals and flex your progress' },
    { src: '/single_goal_feed_or_progress_feed.webp', title: 'Progress Feed', description: 'Every update for a goal, all in one scroll' },
    { src: '/tiktok_style_goal_updates.webp', title: 'Goal Updates', description: 'Share progress TikTok-style' },
    { src: '/fun_reactions_to_goal_updates.webp', title: 'Reactions', description: 'Hype up your friends with fun reactions' },
  ];

  useEffect(() => {
    if (screenPaused) return;
    const interval = setInterval(() => {
      setActiveScreen(prev => (prev + 1) % screens.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [screens.length, screenPaused]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    setError('');
    try {
      const success = await saveEmail(email);
      if (success) {
        setIsSubmitted(true);
        setEmail('');
        setTimeout(() => { setIsSubmitted(false); setShowEmailModal(false); }, 3000);
      } else {
        setError('Failed to save. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const isVisible = (id) => visibleSections.has(id);

  const features = [
    {
      icon: Target,
      title: 'Goal Details',
      headline: 'All your goal details in one place.',
      description: "Everything about your goal — progress, milestones, streaks, and stats — all on one beautiful screen. No digging around, just tap and see exactly where you stand.",
      color: 'amber',
      gradient: 'from-amber-500 to-orange-500',
      bgLight: 'bg-amber-50',
      borderLight: 'border-amber-200',
      textColor: 'text-amber-600',
      screenshots: ['/goal_detail.webp', '/goal_detail2.webp'],
    },
    {
      icon: BookOpen,
      title: 'Goal Resources',
      headline: 'Your secret weapon vault.',
      description: "Save YouTube video links, website links, and articles right to your goals. Get a session coach that builds your action plan and tells you exactly what to do next. It's like having a personal trainer for literally anything.",
      color: 'sky',
      gradient: 'from-sky-500 to-blue-500',
      bgLight: 'bg-sky-50',
      borderLight: 'border-sky-200',
      textColor: 'text-sky-600',
      screenshots: ['/goal_resources.webp', '/goal_resources2.webp'],
    },
    {
      icon: TrendingUp,
      title: 'Goal Intelligence',
      headline: 'Your goals, visualized.',
      description: "See trends, patterns, and insights across all your goals. GoalHive crunches the numbers so you can see what's working, what's not, and where to double down.",
      color: 'purple',
      gradient: 'from-purple-500 to-indigo-500',
      bgLight: 'bg-purple-50',
      borderLight: 'border-purple-200',
      textColor: 'text-purple-600',
      screenshots: ['/goal_intelligence_overview.webp', '/goal_intelligence_trends.webp'],
    },
    {
      icon: Brain,
      title: 'AI Coach',
      headline: 'A coach that knows your goals.',
      description: "Create goals, build action plans, and get real feedback — all through conversation. Your AI coach has access to your notes, check-ins, and goal details, so every suggestion is actually relevant. It's like a personal assistant that never forgets.",
      color: 'pink',
      gradient: 'from-pink-500 to-rose-500',
      bgLight: 'bg-pink-50',
      borderLight: 'border-pink-200',
      textColor: 'text-pink-600',
      screenshots: ['/ai_chat.webp', '/ai_chat2.webp'],
    },
    {
      icon: Users,
      title: 'Accountability',
      headline: 'Your hive keeps you going.',
      description: "Team check-ins to keep everyone on track. Nudge your friends when they're slacking. Get nudged back when you need a boost. Real accountability that actually feels fun.",
      color: 'emerald',
      gradient: 'from-emerald-500 to-teal-500',
      bgLight: 'bg-emerald-50',
      borderLight: 'border-emerald-200',
      textColor: 'text-emerald-600',
      screenshots: ['/team_checkins.webp', '/send_nudge.webp'],
    },
  ];

  return (
    <div className="relative bg-white text-[#0a0a0a] overflow-x-hidden antialiased">

      {/* ========== NAVIGATION ========== */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-2xl border-b border-black/5">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain rounded-xl" />
              <span className="text-xl font-bold tracking-tight">GoalHive</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#screenshots" className="text-sm font-medium text-[#666] hover:text-[#0a0a0a] transition-colors">Explore</a>
              <a href="#features" className="text-sm font-medium text-[#666] hover:text-[#0a0a0a] transition-colors">Why GoalHive</a>
              <a href="#download" className="text-sm font-medium text-[#666] hover:text-[#0a0a0a] transition-colors">Download</a>
            </div>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 px-5 bg-[#0a0a0a] text-white text-sm font-semibold rounded-full flex items-center gap-2 hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              <span className="hidden sm:inline">Download for iPhone</span>
              <span className="sm:hidden">iPhone</span>
            </a>
          </div>
        </div>
      </nav>

      {/* ========== EMAIL MODAL ========== */}
      {showEmailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowEmailModal(false)} />
          <div className="relative w-full max-w-md animate-modal-in">
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-black/5">
              <button
                onClick={() => setShowEmailModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#f5f5f5] hover:bg-[#e5e5e5] flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-[#666]" />
              </button>

              {!isSubmitted ? (
                <>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 mb-4">
                      <Rocket className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0a0a0a] mb-1">Join the Waitlist</h3>
                    <p className="text-sm text-[#666]">Get notified about updates and new features</p>
                  </div>

                  <form onSubmit={handleEmailSubmit} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#999]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        className="w-full pl-11 pr-4 py-3.5 bg-[#f5f5f5] border border-[#e5e5e5] rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-[#0a0a0a] placeholder-[#999] text-sm transition-all"
                      />
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">{error}</div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3.5 bg-[#0a0a0a] text-white rounded-xl font-semibold text-sm hover:bg-[#171717] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />Joining...</>
                      ) : (
                        <>Join Waitlist<ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </form>

                  <div className="mt-5 pt-4 border-t border-[#f0f0f0]">
                    <p className="text-xs text-[#999] text-center mb-3">Or download the app now</p>
                    <AppStoreButton className="w-full justify-center !h-12 !rounded-xl !text-sm" />
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
                    <CheckCircle className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-1">You're in!</h3>
                  <p className="text-sm text-[#666]">We'll keep you posted on all the good stuff.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========== HERO ========== */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-16 overflow-hidden"
      >
        {/* Honeycomb background */}
        <div className="absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='98' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23d4d4d4' fill-opacity='1'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text */}
          <div className={`transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100/80 backdrop-blur-sm border border-amber-200/60 mb-8">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-sm font-semibold text-amber-800">Now live on the App Store</span>
            </div>

            <h1 className="text-[3.2rem] md:text-7xl lg:text-[5.5rem] font-extrabold tracking-tight leading-[0.92] mb-6">
              Your Goals{' '}
              <span className="block mt-1">Just Got</span>
              <span className="block mt-1 bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 bg-clip-text text-transparent pb-2">
                Way More Fun.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#555] mb-10 max-w-lg leading-relaxed">
              The social app that helps you achieve more, together. Set goals, share progress, and grow with your people. <span className="font-semibold text-[#0a0a0a]">Free to download.</span>
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-10">
              <AppStoreButton />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="text-sm text-[#666]">Loved by early users</span>
            </div>
          </div>

          {/* Phone */}
          <div
            className={`relative flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            <div
              className="relative"
              style={{
                transform: `perspective(1200px) rotateY(${mousePosition.x * 4}deg) rotateX(${-mousePosition.y * 3}deg)`,
                transition: 'transform 0.3s ease-out',
              }}
            >
              <PhoneMockup src="/goalhive_home.webp" className="scale-90 md:scale-125" />

              {/* Floating cards */}
              <div className="hidden md:block absolute -left-16 bottom-[34%] bg-white rounded-2xl shadow-xl shadow-black/8 p-3.5 border border-black/5 animate-float z-20">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Flame className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold">12-day streak!</div>
                    <div className="text-[10px] text-[#999]">Keep it going</div>
                  </div>
                </div>
              </div>

              <div className="hidden md:block absolute -right-12 bottom-[6%] bg-white rounded-2xl shadow-xl shadow-black/8 p-3.5 border border-black/5 animate-float animation-delay-2000 z-20">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-xs font-bold">Goal crushed!</div>
                    <div className="text-[10px] text-[#999]">Run 5K</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <button
          onClick={() => document.getElementById('screenshots')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce cursor-pointer"
        >
          <span className="text-xs text-[#bbb]">Scroll</span>
          <ChevronDown className="w-5 h-5 text-[#ccc]" />
        </button>
      </section>

      {/* ========== MARQUEE ========== */}
      <section className="py-5 bg-[#0a0a0a] overflow-hidden select-none">
        <div className="animate-marquee flex items-center gap-8 whitespace-nowrap">
          {[...Array(3)].map((_, setIdx) => (
            <div key={setIdx} className="flex items-center gap-8">
              {['SET GOALS', 'BUILD HABITS', 'AI COACH', 'CRUSH IT', 'FIND YOUR HIVE', 'TRACK STREAKS', 'LEVEL UP'].map((text, i) => (
                <React.Fragment key={`${setIdx}-${i}`}>
                  <span className="text-xl md:text-2xl font-bold text-white/90 tracking-wide">{text}</span>
                  <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ========== SCREENSHOT GALLERY ========== */}
      <section id="screenshots" className="py-24 md:py-32 overflow-hidden" data-section="gallery">
        <div className="max-w-7xl mx-auto px-6 mb-14 text-center">
          <h2 className={`text-4xl md:text-6xl font-extrabold tracking-tight mb-4 transition-all duration-700 ${isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            See what's <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">inside.</span>
          </h2>
          <p className={`text-lg text-[#666] max-w-md mx-auto transition-all duration-700 delay-200 ${isVisible('gallery') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            It's like social media, but for your goals.
          </p>
        </div>

        {/* Mobile: single big phone */}
        <div className="md:hidden flex justify-center mb-6">
          <div className="relative">
            {screens.map((screen, i) => (
              <div
                key={i}
                className={`transition-all duration-500 ${
                  activeScreen === i
                    ? 'opacity-100 scale-100 relative'
                    : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                }`}
              >
                <PhoneMockup src={screen.src} />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: animated spread layout */}
        <div className="hidden md:flex justify-center gap-10 px-4 mb-10 overflow-visible pb-4">
          {screens.map((screen, i) => {
            const isActive = activeScreen === i;
            const rotations = [-8, -4, 0, 4, 8];

            return (
              <div
                key={i}
                onClick={() => { setActiveScreen(i); pauseScreens(); }}
                className={`flex-shrink-0 cursor-pointer transition-all duration-700 ease-in-out ${isVisible('gallery') ? 'opacity-100' : 'opacity-0 translate-y-16'}`}
                style={{
                  transform: isVisible('gallery')
                    ? `rotate(${isActive ? 0 : rotations[i]}deg) scale(${isActive ? 1.12 : 1}) translateY(${isActive ? -12 : 0}px)`
                    : `rotate(${rotations[i]}deg) translateY(60px)`,
                  transitionDelay: isVisible('gallery') ? `${i * 100 + 300}ms` : '0ms',
                  zIndex: isActive ? 10 : 5 - Math.abs(i - 2),
                }}
              >
                <PhoneMockup src={screen.src} style={{ width: 200, height: 432 }} />
              </div>
            );
          })}
        </div>

        {/* Description area */}
        <div className="text-center h-20 flex flex-col items-center justify-center">
          {screens.map((screen, i) => (
            <div
              key={i}
              className={`transition-all duration-500 ${
                activeScreen === i
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 absolute pointer-events-none'
              }`}
              style={{ position: activeScreen === i ? 'relative' : 'absolute' }}
            >
              <h3 className="text-xl md:text-2xl font-bold mb-1">{screen.title}</h3>
              <p className="text-[#666] text-sm md:text-base">{screen.description}</p>
            </div>
          ))}
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {screens.map((_, i) => (
            <button
              key={i}
              onClick={() => { setActiveScreen(i); pauseScreens(); }}
              className={`h-2 rounded-full transition-all duration-500 ${
                activeScreen === i
                  ? 'w-8 bg-gradient-to-r from-amber-500 to-orange-500'
                  : 'w-2 bg-[#d5d5d5] hover:bg-[#bbb]'
              }`}
            />
          ))}
        </div>
      </section>

      {/* ========== BIG FEATURE SECTION ========== */}
      <section id="features" className="py-24 md:py-32 bg-[#fafafa]" data-section="features">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#e5e5e5] mb-6 transition-all duration-700 ${isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-[#666]">Why GoalHive</span>
            </div>
            <h2 className={`text-4xl md:text-6xl font-extrabold tracking-tight mb-4 transition-all duration-700 delay-100 ${isVisible('features') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Everything you need to
              <span className="block bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 bg-clip-text text-transparent pb-1">actually hit your goals.</span>
            </h2>
          </div>

          {/* Feature tabs */}
          <div className="flex justify-start md:justify-center gap-3 mb-16 overflow-x-auto md:overflow-visible pb-2 px-2 md:px-0 md:flex-wrap">
            {features.map((feat, i) => (
              <button
                key={i}
                onClick={() => { setActiveFeature(i); pauseFeatures(); }}
                className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                  activeFeature === i
                    ? `bg-gradient-to-r ${feat.gradient} text-white shadow-lg scale-105`
                    : 'bg-white text-[#666] border border-[#e5e5e5] hover:border-[#ccc]'
                }`}
              >
                <feat.icon className="w-4 h-4" />
                {feat.title}
              </button>
            ))}
          </div>

          {/* Active feature display */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <div className="relative">
                {features.map((feat, i) => (
                  <div
                    key={i}
                    className={`transition-all duration-500 ${
                      activeFeature === i
                        ? 'opacity-100 scale-100 relative'
                        : 'opacity-0 scale-95 absolute inset-0 pointer-events-none'
                    }`}
                  >
                    <div className="flex gap-2 md:gap-4 items-end justify-center">
                      <PhoneMockup src={feat.screenshots[0]} style={{ width: 'clamp(140px, 35vw, 280px)', height: 'clamp(303px, 75vw, 606px)' }} />
                      <PhoneMockup src={feat.screenshots[1]} className="opacity-80" style={{ width: 'clamp(126px, 31vw, 252px)', height: 'clamp(272px, 67vw, 545px)' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              {features.map((feat, i) => (
                <div
                  key={i}
                  className={`transition-all duration-500 ${
                    activeFeature === i
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4 absolute pointer-events-none'
                  }`}
                  style={{ position: activeFeature === i ? 'relative' : 'absolute' }}
                >
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${feat.bgLight} border ${feat.borderLight} mb-6`}>
                    <feat.icon className={`w-4 h-4 ${feat.textColor}`} />
                    <span className={`text-sm font-semibold ${feat.textColor}`}>{feat.title}</span>
                  </div>

                  <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-6 leading-tight">
                    {feat.headline}
                  </h3>

                  <p className="text-lg text-[#555] mb-8 leading-relaxed max-w-md">
                    {feat.description}
                  </p>

                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${feat.gradient} text-white font-semibold hover:scale-105 transition-transform shadow-lg`}
                  >
                    Try it free
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== MORE SCREENSHOTS STRIP ========== */}
      <section className="py-24 md:py-32 overflow-hidden" data-section="moreshots">
        <div className="max-w-7xl mx-auto px-6 mb-14">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className={`text-4xl md:text-5xl font-extrabold tracking-tight mb-6 transition-all duration-700 ${isVisible('moreshots') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                Built different.
                <span className="block text-[#999]">On purpose.</span>
              </h2>
              <p className={`text-lg text-[#555] mb-8 leading-relaxed max-w-md transition-all duration-700 delay-200 ${isVisible('moreshots') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                Every pixel exists to move you closer to your goals. No distractions. No doom scrolling. Just you, your goals, and the people cheering you on.
              </p>

              <div className={`space-y-4 transition-all duration-700 delay-300 ${isVisible('moreshots') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                {[
                  { icon: Flame, text: 'Streak tracking that keeps you hooked', color: 'text-orange-500 bg-orange-50' },
                  { icon: TrendingUp, text: 'Beautiful progress visualizations', color: 'text-indigo-500 bg-indigo-50' },
                  { icon: Star, text: 'Achievements that feel earned', color: 'text-amber-500 bg-amber-50' },
                  { icon: Heart, text: 'A feed that actually inspires you', color: 'text-pink-500 bg-pink-50' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#f0f0f0] hover:border-[#e0e0e0] hover:shadow-sm transition-all">
                    <div className={`w-10 h-10 rounded-xl ${item.color.split(' ')[1]} flex items-center justify-center`}>
                      <item.icon className={`w-5 h-5 ${item.color.split(' ')[0]}`} />
                    </div>
                    <span className="font-medium text-[#333]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <div className={`transition-all duration-700 delay-200 ${isVisible('moreshots') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                <PhoneMockup src="/checkin_success.webp" style={{ width: 'clamp(150px, 35vw, 220px)', height: 'clamp(306px, 71vw, 448px)' }} />
              </div>
              <div className={`mt-12 transition-all duration-700 delay-400 ${isVisible('moreshots') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                <PhoneMockup src="/public_trophy_case.webp" style={{ width: 'clamp(150px, 35vw, 220px)', height: 'clamp(306px, 71vw, 448px)' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF / STATS ========== */}
      <section className="py-20 bg-[#0a0a0a] text-white relative overflow-hidden" data-section="stats">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(245,158,11,0.15),rgba(0,0,0,0))]" />

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className={`text-4xl md:text-5xl font-extrabold tracking-tight mb-4 transition-all duration-700 ${isVisible('stats') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              People are already <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">crushing it.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {[
              { value: '100+', label: 'Beta testers', icon: Users },
              { value: '500+', label: 'Goals set', icon: Target },
              { value: '150+', label: 'Streaks active', icon: Flame },
              { value: '5', label: 'App rating', icon: Star },
            ].map((stat, i) => (
              <div
                key={i}
                className={`text-center p-6 rounded-2xl bg-white/5 border border-white/10 transition-all duration-700 hover:bg-white/10 ${isVisible('stats') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100 + 200}ms` }}
              >
                <stat.icon className="w-6 h-6 text-amber-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-extrabold mb-1">{stat.value}</div>
                <div className="text-sm text-white/50">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== COMPARISON ========== */}
      <section className="py-24 md:py-32" data-section="compare">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`text-center mb-16 transition-all duration-700 ${isVisible('compare') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight">
              Not your typical <span className="text-[#999]">social media.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-8 rounded-3xl bg-[#fafafa] border border-[#e5e5e5] transition-all duration-700 delay-100 ${isVisible('compare') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-[#999]" />
                <span className="font-semibold text-[#666]">Other Apps</span>
              </div>
              <ul className="space-y-4">
                {['Endless scrolling', 'Comparison culture', 'Time wasted', 'Addictive by design', 'Highlight reels'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#666]">
                    <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-8 rounded-3xl bg-[#0a0a0a] text-white transition-all duration-700 delay-200 ${isVisible('compare') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="font-semibold">GoalHive</span>
              </div>
              <ul className="space-y-4">
                {['Intentional engagement', 'Celebration culture', 'Time invested in you', 'Growth by design', 'Real progress, real people'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section id="download" className="relative py-32 md:py-40 bg-gradient-to-br from-amber-500 via-orange-500 to-pink-500 overflow-hidden" data-section="cta">
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`text-center lg:text-left transition-all duration-700 ${isVisible('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6 leading-tight">
                Your goals called.
                <span className="block opacity-80">They miss you.</span>
              </h2>

              <p className="text-lg text-white/80 mb-10 max-w-md mx-auto lg:mx-0">
                Download GoalHive and start turning your biggest dreams into daily wins. It's free. No excuses.
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
                <AppStoreButton dark={false} />
              </div>

              <p className="text-white/50 text-sm mt-8">
                Free to download. Built for your goals.
              </p>
            </div>

            <div className={`flex justify-center transition-all duration-700 delay-300 ${isVisible('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className="relative">
                <PhoneMockup src="/goalhive_welcome.webp" className="scale-90 md:scale-125" />
                <div className="absolute -inset-4 rounded-[3.5rem] bg-white/10 blur-xl -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECOND MARQUEE ========== */}
      <section className="py-4 bg-[#0a0a0a] overflow-hidden select-none">
        <div className="animate-marquee-reverse flex items-center gap-8 whitespace-nowrap">
          {[...Array(3)].map((_, setIdx) => (
            <div key={setIdx} className="flex items-center gap-8">
              {['FREE DOWNLOAD', 'AI POWERED', 'GOAL TRACKING', 'SOCIAL', 'STREAKS', 'ACHIEVEMENTS', 'LEVEL UP'].map((text, i) => (
                <React.Fragment key={`${setIdx}-${i}`}>
                  <span className="text-lg font-bold text-white/60 tracking-wide">{text}</span>
                  <span className="text-amber-400">+</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="py-12 bg-white border-t border-[#f0f0f0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain rounded-xl" />
              <span className="text-xl font-bold text-[#0a0a0a]">GoalHive</span>
            </div>

            <div className="flex items-center gap-8 text-sm text-[#666]">
              <a href="/support" className="hover:text-[#0a0a0a] transition-colors">Support</a>
              <a href="/privacy" className="hover:text-[#0a0a0a] transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-[#0a0a0a] transition-colors">Terms</a>
            </div>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-[#666] hover:text-[#0a0a0a] flex items-center gap-1.5 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download App
            </a>
          </div>

          <div className="mt-8 pt-8 border-t border-[#f0f0f0] text-center text-sm text-[#999]">
            &copy; {new Date().getFullYear()} GoalHive LLC. All rights reserved.
          </div>
        </div>
      </footer>

      {/* ========== ANIMATIONS ========== */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 8s ease-in-out infinite; }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(var(--rotate, 0deg)); }
          50% { transform: translateY(-15px) rotate(var(--rotate, 0deg)); }
        }
        .animate-float { animation: float 4s ease-in-out infinite; }

        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .animate-marquee { animation: marquee 25s linear infinite; }

        @keyframes marquee-reverse {
          0% { transform: translateX(-33.333%); }
          100% { transform: translateX(0); }
        }
        .animate-marquee-reverse { animation: marquee-reverse 30s linear infinite; }

        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in { animation: modal-in 0.25s ease-out; }

        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-3000 { animation-delay: 3s; }
        .animation-delay-4000 { animation-delay: 4s; }

        /* Hide scrollbar on screenshot gallery */
        #screenshots .flex::-webkit-scrollbar { display: none; }
        #screenshots .flex { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default App7;
